<!--
  Client Portal Phase 4: branded manual-login fallback.

  The primary client-portal entry is the /portal/invite/:token magic-link
  flow (see AutoLogin.vue). This page is the demo-only fallback shown
  when a Client Admin lands at /portal/login directly.

  IMPORTANT (demo-only): this form does NOT call any auth endpoint. On
  submit we surface a transient toast ("Demo login - not wired") and
  push the route to /portal/overview. The real login wiring lands when
  the Phase-4 backend auth endpoint exists.

  Branding uses mockClientProfile.brand_color as the accent. When the
  /api/portal/branding endpoint lands, swap that import for a Pinia
  store hydrated by the API.
-->
<template>
  <v-container
    fluid
    class="portal-login fill-height"
    data-testid="portal-page-login"
  >
    <v-row align="center" justify="center" class="fill-height">
      <v-col cols="12" sm="8" md="5" lg="4">
        <MockDataBanner surface="Client Portal Login" />

        <v-card class="pa-6" data-testid="portal-login-card">
          <h1
            class="text-h5 mb-1"
            data-testid="portal-login-brand"
            :style="{ color: brandColor }"
          >
            {{ clientName }}
          </h1>
          <div class="text-caption text-medium-emphasis mb-6">
            Sign in to the Client Portal
          </div>

          <v-form @submit.prevent="onSubmit">
            <v-text-field
              v-model="email"
              label="Email"
              type="email"
              variant="outlined"
              density="comfortable"
              data-testid="portal-login-email"
              class="mb-3"
            />
            <v-text-field
              v-model="password"
              label="Password"
              type="password"
              variant="outlined"
              density="comfortable"
              data-testid="portal-login-password"
              class="mb-4"
            />
            <v-btn
              type="submit"
              block
              :color="brandColor"
              data-testid="portal-login-submit"
            >
              Sign in
            </v-btn>
          </v-form>

          <div
            v-if="demoToast"
            class="mt-4 text-body-2 text-center"
            data-testid="portal-login-demo-toast"
          >
            {{ demoToast }}
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import MockDataBanner from "@/components/MockDataBanner.vue";
import { mockClientProfile } from "@/utils/mockData/clientPortalPhase4";

export default {
  name: "PortalLogin",
  components: { MockDataBanner },
  data() {
    return {
      email: "",
      password: "",
      demoToast: "",
    };
  },
  computed: {
    clientName() {
      return mockClientProfile.client_name;
    },
    brandColor() {
      return mockClientProfile.brand_color;
    },
  },
  beforeUnmount() {
    if (this._navTimer) {
      clearTimeout(this._navTimer);
      this._navTimer = null;
    }
  },
  methods: {
    onSubmit() {
      // Demo-only: do NOT call any auth endpoint. Surface a toast and
      // redirect so the IA is wired without simulating real auth.
      this.demoToast = "(Demo login - not wired)";
      // Defer route push so the toast paints; in test environments the
      // router stub may be absent, so guard the call.
      if (this.$router && typeof this.$router.push === "function") {
        // Brief setTimeout so the toast is observable before navigation.
        // Tracked on the instance so beforeUnmount can cancel it if the
        // user navigates away within the window.
        this._navTimer = setTimeout(() => {
          this._navTimer = null;
          this.$router.push("/portal/overview");
        }, 50);
      }
    },
  },
};
</script>
